import React, { useEffect, useMemo, useState } from 'react';
import type { TablePaginationConfig } from 'antd';
import Filters from 'works/legacy/lib/filters';
import {
  RenderFilteredTable,
  type RenderTableOnChange,
} from './render-filtered-table';
import type { FilteredTableProps } from './_filtered-table-types';

export type {
  FilterTableOptionType,
  FilteredTableProps,
} from './_filtered-table-types';
export {
  buildOptionsFromData,
  filtersForPaginatedAttr,
} from './_filtered-table-service';

export function FilteredTable<T extends object>({
  loading,
  pagination,
  onChange,
  onFilterChange,
  localStorageFilterKey,
  filters,
  columns,
  rowKey = 'key',
  ...props
}: FilteredTableProps) {
  const defaultControlFilters =
    filters ??
    ((localStorageFilterKey && Filters.getFilters(localStorageFilterKey)) ||
      {});

  const [controlFilters, setControlFilters] = useState<Record<string, unknown>>(
    defaultControlFilters
  );
  const [sortingAttrs, setSortingAttrs] = useState<
    Parameters<NonNullable<RenderTableOnChange<T>>>[2]
  >({} as Parameters<NonNullable<RenderTableOnChange<T>>>[2]);
  const [pageNumber, setPageNumber] = useState(pagination.current || 1);
  const [pageSize, setPageSize] = useState(
    pagination.pageSize || pagination.defaultPageSize || 10
  );

  useEffect(() => {
    setControlFilters(
      filters ??
        ((localStorageFilterKey && Filters.getFilters(localStorageFilterKey)) ||
          {})
    );
  }, [filters, localStorageFilterKey]);

  const internalPagination = useMemo(
    () => ({
      ...pagination,
      showSizeChanger: true,
      current: pageNumber,
      pageSize,
      onChange: (selectedPageNumber: number) => {
        setPageNumber(selectedPageNumber);
      },
      onShowSizeChange: (_current: number, size: number) => {
        setPageNumber(1);
        setPageSize(size);
      },
    }),
    [pageNumber, pageSize, pagination]
  );

  const defaultPagination: TablePaginationConfig & {
    staffingRequestId?: unknown;
  } = {
    staffingRequestId: internalPagination.staffingRequestId,
    current: internalPagination.current || 1,
    pageSize,
  };

  const updateSorter = (
    sorter: Parameters<NonNullable<RenderTableOnChange<T>>>[2]
  ) => {
    sorter && setSortingAttrs(sorter);
    return sorter || sortingAttrs;
  };

  const onDefaultChange: RenderTableOnChange<T> = (
    nextPagination,
    tableFilters,
    sorter,
    extra
  ) => {
    setControlFilters(tableFilters as Record<string, unknown>);
    onFilterChange?.(tableFilters);
    onChange?.(nextPagination, tableFilters, updateSorter(sorter), extra);
  };

  const clearAllFilters = () => {
    onFilterChange?.({});
    setPageNumber(1);
    onDefaultChange(
      { ...defaultPagination, current: 1, pageSize },
      {},
      null as unknown as Parameters<NonNullable<RenderTableOnChange<T>>>[2],
      {
        currentDataSource: [],
        action: 'filter',
      } as Parameters<NonNullable<RenderTableOnChange<T>>>[3]
    );
  };

  const clearFilter = (
    column: string,
    value?: string | number | boolean | null
  ) => {
    const columnFilters = controlFilters[column] as unknown[] | undefined;

    let remaining: unknown[] | null = value
      ? (columnFilters?.filter((item) => item !== value) ?? [])
      : [];

    if (remaining.length === 0) {
      remaining = null;
    }

    setPageNumber(1);
    onDefaultChange(
      { ...defaultPagination, current: 1, pageSize },
      { ...controlFilters, [column]: remaining } as Parameters<
        NonNullable<RenderTableOnChange<T>>
      >[1],
      null as unknown as Parameters<NonNullable<RenderTableOnChange<T>>>[2],
      {
        currentDataSource: [],
        action: 'filter',
      } as Parameters<NonNullable<RenderTableOnChange<T>>>[3]
    );
  };

  return (
    <RenderFilteredTable<T>
      {...props}
      rowKey={rowKey}
      columns={columns}
      filters={controlFilters}
      onClearAllFilters={clearAllFilters}
      onClearFilter={clearFilter}
      pagination={internalPagination}
      loading={loading}
      onChange={onDefaultChange}
    />
  );
}
